import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Tag, Check, CreditCard, Wallet, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useStore } from "@/context/StoreContext";
import { cartSavings, minPurchaseFor } from "@/lib/pricing";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const navigate = useNavigate();
  const { lines, subtotal, shipping, total, verified, altosId, clear } = useStore();

  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [paymentMode, setPaymentMode] = useState("full");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null); // {code, discount}
  const [available, setAvailable] = useState([]);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(false);

  const minPurchase = minPurchaseFor(verified);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const effectiveMin = settings ? (verified ? settings.min_purchase_altos : settings.min_purchase_regular) : minPurchase;
  const savings = cartSavings(lines, verified);
  const discount = applied?.discount || 0;
  const grand = Math.max(0, total - discount);
  const advance = paymentMode === "partial_cod" ? Math.round(grand * 0.3) : grand;
  const codDue = paymentMode === "partial_cod" ? grand - advance : 0;

  useEffect(() => {
    if (lines.length === 0 && !placed) navigate("/cart");
  }, [lines.length, navigate, placed]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fetchCoupons = async () => {
    if (!form.phone) { toast.error("Enter your mobile number first"); return; }
    const list = await api.availableCoupons(verified, form.phone, subtotal);
    setAvailable(list);
    if (list.length === 0) toast("No coupons available for you right now");
  };

  const applyCoupon = async (code) => {
    try {
      const res = await api.validateCoupon({ code: code || coupon, phone: form.phone, altos_verified: verified, subtotal });
      setApplied({ code: res.code, discount: res.discount });
      setCoupon(res.code);
      toast.success(`Coupon ${res.code} applied — ${formatINR(res.discount)} off`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const validate = () => {
    if (!form.name.trim()) return "Enter your name";
    if (!/.+@.+\..+/.test(form.email)) return "Enter a valid email";
    if (form.phone.replace(/\D/g, "").length < 10) return "Enter a valid 10-digit mobile number";
    if (form.address.trim().length < 5) return "Enter your full delivery address";
    if (subtotal < effectiveMin) return `Minimum order is ${formatINR(effectiveMin)}`;
    return null;
  };

  const placeOrder = async () => {
    const v = validate();
    if (v) { toast.error(v); return; }
    setPlacing(true);
    try {
      const payload = {
        items: lines.map((l) => ({ id: l.product.id, quantity: l.quantity })),
        customer: { ...form, altos_id: altosId || "" },
        altos_verified: verified,
        coupon_code: applied?.code || "",
        payment_mode: verified ? "full" : paymentMode,
      };
      const order = await api.createOrder(payload);

      if (order.demo) {
        setPlaced(true);
        await api.demoComplete(order.order_id);
        navigate(`/order-success/${order.order_id}`);
        clear();
        return;
      }

      // Real Razorpay flow
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setPlacing(false); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "Altos World Online Store",
        order_id: order.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#3E8E4C" },
        handler: async (resp) => {
          try {
            setPlaced(true);
            await api.verify({
              order_id: order.order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            navigate(`/order-success/${order.order_id}`);
            clear();
          } catch (err) {
            toast.error(err.message);
          }
        },
        modal: { ondismiss: () => setPlacing(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message);
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <h1 className="font-display text-4xl sm:text-5xl text-ink mb-6">Checkout</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery details */}
            <section className="rounded-2xl border border-line p-6">
              <h2 className="font-display text-2xl text-ink mb-4">Delivery Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.name} onChange={set("name")} placeholder="Full name" className="h-12 rounded-lg border border-line px-4 text-sm focus:outline-none focus:border-brand" data-testid="checkout-name" />
                <input value={form.phone} onChange={set("phone")} placeholder="Mobile number" className="h-12 rounded-lg border border-line px-4 text-sm focus:outline-none focus:border-brand" data-testid="checkout-phone" />
                <input value={form.email} onChange={set("email")} placeholder="Email address" className="h-12 rounded-lg border border-line px-4 text-sm focus:outline-none focus:border-brand sm:col-span-2" data-testid="checkout-email" />
                <textarea value={form.address} onChange={set("address")} placeholder="Full delivery address with PIN code" rows={3} className="rounded-lg border border-line px-4 py-3 text-sm focus:outline-none focus:border-brand sm:col-span-2" data-testid="checkout-address" />
              </div>
            </section>

            {/* Coupon */}
            <section className="rounded-2xl border border-line p-6">
              <h2 className="font-display text-2xl text-ink mb-4 flex items-center gap-2"><Tag size={20} className="text-brand" /> Coupon</h2>
              <div className="flex gap-2">
                <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Enter code" className="flex-1 h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand" data-testid="coupon-input" />
                <button onClick={() => applyCoupon()} className="h-11 px-5 rounded-lg bg-ink text-white text-sm font-semibold" data-testid="coupon-apply">Apply</button>
                <button onClick={fetchCoupons} className="h-11 px-4 rounded-lg border border-line text-sm font-semibold text-brand" data-testid="coupon-view">View offers</button>
              </div>
              {applied && <p className="text-sm text-ok mt-2 flex items-center gap-1"><Check size={15} /> {applied.code} applied — {formatINR(applied.discount)} off</p>}
              {available.length > 0 && (
                <div className="mt-3 space-y-2">
                  {available.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2" data-testid={`coupon-offer-${c.code}`}>
                      <div>
                        <span className="font-semibold text-ink text-sm">{c.code}</span>
                        <span className="text-xs text-muted2 ml-2">{c.description}</span>
                      </div>
                      <button disabled={!c.eligible} onClick={() => applyCoupon(c.code)} className="text-sm font-semibold text-brand disabled:text-muted2 disabled:opacity-60">
                        {c.eligible ? "Apply" : "Not eligible"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Payment mode */}
            {!verified && (
              <section className="rounded-2xl border border-line p-6">
                <h2 className="font-display text-2xl text-ink mb-4">Payment</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button onClick={() => setPaymentMode("full")} className={`text-left rounded-xl border-2 p-4 transition-colors ${paymentMode === "full" ? "border-brand bg-mist" : "border-line"}`} data-testid="pay-full">
                    <CreditCard className="text-brand mb-2" size={20} />
                    <p className="font-semibold text-ink text-sm">Pay Full Online</p>
                    <p className="text-xs text-muted2">Pay {formatINR(grand)} securely via Razorpay</p>
                  </button>
                  <button onClick={() => setPaymentMode("partial_cod")} className={`text-left rounded-xl border-2 p-4 transition-colors ${paymentMode === "partial_cod" ? "border-brand bg-mist" : "border-line"}`} data-testid="pay-partial">
                    <Wallet className="text-brand mb-2" size={20} />
                    <p className="font-semibold text-ink text-sm">Partial COD</p>
                    <p className="text-xs text-muted2">Pay 30% now, rest on delivery</p>
                  </button>
                </div>
              </section>
            )}
          </div>

          {/* Summary */}
          <div className="rounded-2xl border border-line p-6 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
            <h2 className="font-display text-2xl text-ink mb-4">Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted2">Subtotal</span><span>{formatINR(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted2">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
              {discount > 0 && <div className="flex justify-between text-ok"><span>Discount</span><span>- {formatINR(discount)}</span></div>}
              <div className="border-t border-line my-2" />
              <div className="flex justify-between text-base font-semibold text-ink"><span>Total</span><span>{formatINR(grand)}</span></div>
              {paymentMode === "partial_cod" && !verified && (
                <div className="mt-2 rounded-lg bg-mist p-3 text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-muted2">Pay now (30%)</span><span className="font-semibold text-ink">{formatINR(advance)}</span></div>
                  <div className="flex justify-between"><span className="text-muted2">On delivery</span><span className="font-semibold text-ink">{formatINR(codDue)}</span></div>
                </div>
              )}
            </div>
            {savings > 0 && <div className="mt-3 rounded-lg bg-brandter text-ok text-sm font-semibold px-3 py-2 text-center">Total savings: {formatINR(savings + discount)}</div>}
            <button disabled={placing} onClick={placeOrder} data-testid="place-order-button" className="w-full h-12 mt-4 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-brand/90 transition-colors">
              {placing ? <><Loader2 className="animate-spin" size={18} /> Processing…</> : <>Pay {formatINR(advance)}</>}
            </button>
            <p className="text-[11px] text-muted2 mt-2 text-center">Secure payments via Razorpay.</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
